<script setup>
import ProductHeroImage from '@/components/basic/atoms/ProductHeroImage.vue'
import { useGlobals } from '@/composables/useGlobals.js'
import ElbwalkerHelper from '@/services/helpers/ElbwalkerHelper'
import { usePdpStore } from '@/stores/pdp'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay, useGoTo } from 'vuetify'

// Critical above-the-fold components - direct imports for faster LCP
import Grape from '@/components/basic/atoms/Grape.vue'
import AggregatedRating from '@/components/basic/molecules/AggregatedRating.vue'
import Cta from '@/components/basic/molecules/Cta.vue'
import DeliveryInfo from '@/components/basic/molecules/DeliveryInfo.vue'
import MilesAndMoreBanner from '@/components/basic/molecules/MilesAndMoreBanner.vue'
import Price from '@/components/basic/molecules/Price.vue'
import TheBanners from '@/components/pdp/molecules/TheBanners.vue'

// Below-the-fold components - keep async loading
const Legals = defineAsyncComponent(
  () => import('@/components/pdp/atoms/Legals.vue')
)
const AddToWishlist = defineAsyncComponent(
  () => import('@/components/basic/atoms/AddToWishlist.vue')
)
const AwardWrapper = defineAsyncComponent(
  () => import('@/components/basic/molecules/AwardWrapper.vue')
)
const VicampoLink = defineAsyncComponent(
  () => import('@/components/basic/atoms/VicampoLink.vue')
)

const router = useRouter()

const { smAndDown, lgAndUp, mdAndDown } = useDisplay()

const goTo = useGoTo()
const { translate } = useGlobals()

const pdpStore = usePdpStore()

const {
  apiProduct,
  apiV1Product,
  metaProduct,
  calculated,
  getFirstAvailableStock: stock,
  loading,
  isPriceUpdating: priceUpdating
} = storeToRefs(pdpStore)

const productStageEl = ref(null)

const pId = computed(() => {
  return router.currentRoute.value.params?.pId
})
const cId = computed(() => {
  return router.currentRoute.value.params?.cId
})

const milesAndMoreData = computed(() => {
  return metaProduct.value.milesandmore
})

const recommendation = computed(() => {
  return metaProduct.value.recommendation
})

const manufacturerUrl = computed(() => {
  return apiProduct.value?.manufacturer_link
})

const isBundle = computed(() => {
  return calculated.value.isBundle
})

const tags = computed(() => {
  return calculated.value.tags
})

const elbData = computed(() => {
  return ElbwalkerHelper.getProductData(apiProduct.value)
})

const showManufacturer = computed(() => {
  return apiProduct.value.manufacturer_enabled
})

// immediate: on a server-rendered entry the product arrives with
// __PINIA_STATE__ before this component mounts, so a lazy watcher never fires
// and the impression event is lost. The element check keeps it inert during
// the server render itself (VIP-12038).
watch(
  apiProduct,
  async () => {
    // Only for a product that actually loaded: the store starts on an empty
    // shell, so firing on that would send an impression with null ids (cold
    // SPA entries and failed server loads) and a second one once the real
    // product arrives (VIP-12038).
    if (!apiProduct.value?.product_id) return
    await nextTick()
    // window.elb is guarded because the immediate run above also fires in
    // environments that never load the plugin (VIP-12038).
    if (productStageEl.value?.$el && typeof window.elb === 'function') {
      window.elb('product visible', productStageEl.value.$el)
    }
  },
  { immediate: true }
)
</script>

<template>
  <VContainer
    ref="productStageEl"
    id="product-stage"
    class="pa-0 d-flex flex-column"
    data-elb="product"
    :data-elb-product="elbData"
  >
    <VSheet class="d-flex flex-column overflow-hidden flex-grow-1">
      <VRow v-if="!loading" class="flex-grow-0">
        <TheBanners />
      </VRow>
      <VRow class="d-flex px-3 mt-3 mt-lg-0 flex-grow-0">
        <div
          class="v-col-12 v-col-lg-6 image-wrapper position-relative overflow-hidden"
        >
          <Grape
            class="bottom-0 top-0 left-0 right-0 position-absolute"
            color="light-beige"
          />
          <ProductHeroImage
            :product-id="pId"
            :campaign-id="cId"
            :alt="apiProduct.name"
            :height="mdAndDown ? 360 : 600"
            :is-bundle="isBundle"
            class="flex-grow-0 product-image-spacing"
            schema-image
          />
          <div class="wishlist-icon">
            <AddToWishlist
              class="flex-row-reverse f-gray c-gray"
              :product="metaProduct"
            />
          </div>
          <AwardWrapper
            v-if="!loading"
            class="award-list mt-4 pl-3 position-absolute"
            :product="apiProduct"
            :threshold="3"
            :max-awards="5"
          />
        </div>
        <div
          class="v-col-12 v-col-lg-6 py-4 c-darker font-size-regular d-flex justify-center px-lg-0"
        >
          <div class="v-col-12 v-col-sm-9 v-col-lg-10 px-0">
            <div class="d-flex flex-column align-center align-lg-start">
              <h2
                v-if="apiProduct.manufacturer_name"
                itemprop="brand"
                itemscope=""
                itemtype="https://schema.org/Brand"
                class="mb-0"
              >
                <VicampoLink
                  v-if="showManufacturer"
                  id="manufacturer"
                  class="text-uppercase manufacturer-name link-gray"
                  itemprop="url"
                  :href="manufacturerUrl"
                >
                  <span itemprop="name">{{
                    apiProduct.manufacturer_name
                  }}</span>
                </VicampoLink>
                <span v-else class="manufacturer-name text-uppercase c-gray">
                  {{ apiProduct.manufacturer_name }}
                </span>
              </h2>
              <h1
                itemprop="name"
                id="stage-name"
                class="font-weight-bold mt-3 font-size-xl text-center text-lg-left mb-0"
              >
                {{ apiProduct.name }}
              </h1>
            </div>
            <AggregatedRating
              v-if="!loading && apiV1Product.details.customer_rating"
              class="my-4 justify-center justify-lg-start"
              :aria-label="translate('aria_label_aggregated_rating')"
              include-props
              :rating-data="{
                ratingsAverage: apiV1Product.details.customer_rating,
                ratingsCount: apiV1Product.details.customer_rating_count
              }"
              :show-dropdown-icon="false"
              @click="
                goTo('#customer-reviews', {
                  offset: lgAndUp ? -85 : 0
                })
              "
            />
            <div
              itemprop="description"
              id="reason-why-one"
              class="text-left mt-2 mb-3"
            >
              <p
                class="styled-text ma-0"
                v-html="apiProduct.reason_why_one"
              ></p>
            </div>
            <!-- Shown by CSS, not by the JS breakpoint: a server render has
                 to guess the viewport, and a v-if on that guess hydrates the
                 wrong branch on any desktop window below lg (VIP-12038). This
                 one keeps include-props, so the page has exactly one
                 itemprop=offers even though both variants are in the DOM. -->
            <Price
              class="py-2 d-none d-lg-block"
              :price="calculated.priceObject"
              :stock="stock"
              :loading="priceUpdating"
              include-props
            />
            <MilesAndMoreBanner
              :earn="milesAndMoreData.earn"
              :spend="milesAndMoreData.spend"
            />
            <DeliveryInfo v-if="stock" class="pt-3" :stock="stock" />
            <!-- The breakpoint classes sit on a wrapper, not on Cta itself:
                 its root already carries .d-inline-flex, which beats a d-none
                 on the same element (equal specificity, and vuetify emits
                 .d-inline-flex after .d-none), so the stage CTA stayed visible
                 below lg next to the sticky cart bar (VIP-12038). -->
            <div class="d-none d-lg-block">
              <Cta
                v-if="!loading"
                class="pt-2"
                :pre-selected-qty="calculated.preSelectedQty"
                :product="apiProduct"
              />
            </div>
            <Legals class="d-flex flex-column align-center align-lg-start" />
          </div>
        </div>
      </VRow>
    </VSheet>
  </VContainer>
</template>

<style scoped>
#reason-why-one[loading='true'] {
  min-height: 5rem;
}

a#manufacturer[loading='true'] {
  min-width: 20%;
  min-height: 1.3rem;
}

h1 {
  line-height: var(--xxxl-font-size);
}

h1[loading='true'] {
  min-width: 70%;
  min-height: 1.5rem;
}

.product-image-spacing {
  margin-top: 10px;
}

.award-list {
  top: 0;
  left: 0;
}

.manufacturer-name {
  font-size: var(--m-font-size);
  font-family: var(--area-font-family);
  letter-spacing: 1px;
  font-weight: 700;
}

#product-stage {
  min-height: 600px;
}

.black-bg-mobile {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: opacity var(--transition-duration-l) var(--transition-timing-ease);
}

:deep(.banderole-recommendation svg) {
  display: none;
}

.display-sm-and-up :deep(.banderole-recommendation svg) {
  display: flex;
}

:deep(.mdc-select) {
  min-width: 155px;
}

:deep(.final-price) {
  font-size: var(--xxxl-font-size);
}

:deep(.price-per-unit) {
  font-size: var(--s-font-size);
}

.wishlist-icon {
  position: absolute;
  right: 10px;
  bottom: 10px;
}

.high-contrast {
  filter: contrast(1.5);
}
</style>
